import { EQpHttpStatusCode } from '@library/models/qp-http.models';
import { ActionsQueueSyncService } from '@one/app/components/main/services/actions-queue-sync.service';
import { AuthenticationService } from '@one/app/shared/services/auth/authentication.service';
import { IAM_CONFIG } from '@one/app/shared/tokens/iam-config.token';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { QimaOptionalType } from '@qima/ngx-qima';
import { isEmpty } from 'lodash/index';
import { from, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

/**
 * @description
 * Not provided in root on purpose
 */
@Injectable()
export class AuthExpiredInterceptor implements HttpInterceptor {
  public readonly _authenticationService = inject(AuthenticationService);
  public readonly _actionsQueueSyncService = inject(ActionsQueueSyncService);
  private readonly _iamConfig = inject(IAM_CONFIG, { optional: true });

  public intercept<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    return next.handle(request).pipe(
      catchError((err: unknown): Observable<never> => {
        if (err instanceof HttpErrorResponse && err.status === EQpHttpStatusCode.UNAUTHORIZED) {
          return this._handleUnauthorized(err);
        }

        return throwError((): unknown => err);
      })
    );
  }

  private _handleUnauthorized(error: HttpErrorResponse): Observable<never> {
    if (this._actionsQueueSyncService.syncStackCount$.getValue() > 0) {
      return this._canDeferLogoutWhileSyncPending$().pipe(
        switchMap((canDeferLogout: boolean): Observable<never> => {
          if (canDeferLogout) {
            return throwError((): HttpErrorResponse => error);
          }

          this._authenticationService.handleUnauthorized(error);

          return throwError((): HttpErrorResponse => error);
        }),
        catchError((refreshErr: unknown): Observable<never> => {
          if (refreshErr === error) {
            return throwError((): HttpErrorResponse => error);
          }

          this._authenticationService.handleUnauthorized(error);

          return throwError((): HttpErrorResponse => error);
        })
      );
    }

    this._authenticationService.handleUnauthorized(error);

    return throwError((): HttpErrorResponse => error);
  }

  private _canDeferLogoutWhileSyncPending$(): Observable<boolean> {
    if (!isEmpty(this._iamConfig)) {
      return this._authenticationService.getToken$().pipe(
        take(1),
        map((token: QimaOptionalType<string>): boolean => !!token)
      );
    }

    return from(this._authenticationService.isAuthenticated());
  }
}
